import type { Lang } from "./landing";
import { appUrl } from "../lib/appUrl";
import {
  legalBold as bold,
  legalItem as item,
  legalP as p,
  legalUl as ul,
  type LegalBlock,
  type LegalListItem,
  type LegalSection,
  type LegalTextPart,
} from "./legalDocument";

const TERMS_HREF = appUrl("/terms");

export type PrivacyTextPart = LegalTextPart;
export type PrivacyListItem = LegalListItem;
export type PrivacyBlock = LegalBlock;
export type PrivacySection = LegalSection;

export function privacySections(lang: Lang): PrivacySection[] {
  if (lang === "en") {
    return privacySectionsEn();
  }
  return privacySectionsPl();
}

function privacySectionsPl(): PrivacySection[] {
  return [
    {
      title: "Wstęp",
      blocks: [
        p(
          "Poniższa Polityka Prywatności określa ",
          bold("zasady zapisywania i uzyskiwania dostępu do danych na Urządzeniach Użytkowników"),
          " korzystających z Serwisu do celów świadczenia usług drogą elektroniczną przez Administratora oraz ",
          bold("zasady gromadzenia i przetwarzania danych osobowych Użytkowników"),
          ", które zostały podane przez nich osobiście i dobrowolnie za pośrednictwem narzędzi dostępnych w Serwisie.",
        ),
        p(
          "Poniższa Polityka Prywatności jest integralną częścią ",
          { link: "Regulaminu Serwisu", href: TERMS_HREF, external: true },
          ", który określa zasady, prawa i obowiązki Użytkowników korzystających z Serwisu.",
        ),
      ],
    },
    {
      title: "§1 Definicje",
      blocks: [
        ul(
          item([
            bold("Serwis"),
            ' - serwis internetowy "barmaid" działający pod adresem www.barmaid.io, umożliwiający gościom lokali gastronomicznych składanie zamówień online oraz personelowi lokali zarządzanie menu, miejscami i zamówieniami.',
          ]),
          item([
            bold("Serwis zewnętrzny"),
            " - serwisy internetowe partnerów, usługodawców lub usługobiorców współpracujących z Administratorem",
          ]),
          item([
            bold("Administrator Serwisu / Danych"),
            ' - Administratorem Serwisu oraz Administratorem Danych (dalej Administrator) jest firma "Liftium Sp. z o.o.", prowadząca działalność pod adresem: ul. Floriańska 2/06, 03-707 Warszawa, Polska, o nadanym numerze identyfikacji podatkowej (NIP): 1133153571, o nadanym numerze KRS: 0001143654, świadcząca usługi drogą elektroniczną za pośrednictwem Serwisu',
          ]),
          item([
            bold("Użytkownik"),
            " - osoba fizyczna korzystająca z Serwisu, w tym gość lokalu składający zamówienie oraz personel lokalu posiadający konto w panelu administracyjnym.",
          ]),
          item([bold("Urządzenie"), " - elektroniczne urządzenie wraz z oprogramowaniem, za pośrednictwem którego Użytkownik uzyskuje dostęp do Serwisu"]),
          item([bold("Cookies (ciasteczka)"), " - dane tekstowe gromadzone w formie plików zamieszczanych na Urządzeniu Użytkownika"]),
          item([
            bold("RODO"),
            " - Rozporządzenie Parlamentu Europejskiego i Rady (UE) 2016/679 z dnia 27 kwietnia 2016 r. w sprawie ochrony osób fizycznych w związku z przetwarzaniem danych osobowych i w sprawie swobodnego przepływu takich danych oraz uchylenia dyrektywy 95/46/WE (ogólne rozporządzenie o ochronie danych)",
          ]),
          item([
            bold("Dane osobowe"),
            " - oznaczają informacje o zidentyfikowanej lub możliwej do zidentyfikowania osobie fizycznej; możliwa do zidentyfikowania osoba fizyczna to osoba, którą można bezpośrednio lub pośrednio zidentyfikować, w szczególności na podstawie identyfikatora takiego jak imię i nazwisko, numer identyfikacyjny, dane o lokalizacji, identyfikator internetowy lub jeden bądź kilka szczególnych czynników określających fizyczną, fizjologiczną, genetyczną, psychiczną, ekonomiczną, kulturową lub społeczną tożsamość osoby fizycznej",
          ]),
          item([
            bold("Przetwarzanie"),
            " - oznacza operację lub zestaw operacji wykonywanych na danych osobowych lub zestawach danych osobowych w sposób zautomatyzowany lub niezautomatyzowany, taką jak zbieranie, utrwalanie, organizowanie, porządkowanie, przechowywanie, adaptowanie lub modyfikowanie, pobieranie, przeglądanie, wykorzystywanie, ujawnianie poprzez przesłanie, rozpowszechnianie lub innego rodzaju udostępnianie, dopasowywanie lub łączenie, ograniczanie, usuwanie lub niszczenie",
          ]),
          item([
            bold("Ograniczenie przetwarzania"),
            " - oznacza oznaczenie przechowywanych danych osobowych w celu ograniczenia ich przyszłego przetwarzania",
          ]),
          item([
            bold("Profilowanie"),
            " - oznacza dowolną formę zautomatyzowanego przetwarzania danych osobowych, które polega na wykorzystaniu danych osobowych do oceny niektórych czynników osobowych osoby fizycznej",
          ]),
          item([
            bold("Zgoda"),
            " - zgoda osoby, której dane dotyczą oznacza dobrowolne, konkretne, świadome i jednoznaczne okazanie woli, którym osoba, której dane dotyczą, w formie oświadczenia lub wyraźnego działania potwierdzającego, przyzwala na przetwarzanie dotyczących jej danych osobowych",
          ]),
          item([
            bold("Naruszenie ochrony danych osobowych"),
            " - oznacza naruszenie bezpieczeństwa prowadzące do przypadkowego lub niezgodnego z prawem zniszczenia, utracenia, zmodyfikowania, nieuprawnionego ujawnienia lub nieuprawnionego dostępu do danych osobowych",
          ]),
          item([
            bold("Pseudonimizacja"),
            " - oznacza przetworzenie danych osobowych w taki sposób, by nie można ich było już przypisać konkretnej osobie, której dane dotyczą, bez użycia dodatkowych informacji",
          ]),
          item([
            bold("Anonimizacja"),
            " - nieodwracalny proces operacji na danych, który niszczy lub nadpisuje dane osobowe, uniemożliwiając identyfikację lub powiązanie danego rekordu z konkretnym użytkownikiem lub osobą fizyczną",
          ]),
        ),
      ],
    },
    {
      title: "§2 Inspektor Ochrony Danych",
      blocks: [
        p("Na podstawie art. 37 RODO Administrator nie powołał Inspektora Ochrony Danych."),
        p("W sprawach dotyczących przetwarzania danych, w tym danych osobowych, należy kontaktować się bezpośrednio z Administratorem."),
      ],
    },
    {
      title: "§3 Rodzaje plików Cookies",
      blocks: [
        ul(
          item([bold("Cookies wewnętrzne"), " - pliki zamieszczane i odczytywane z Urządzenia Użytkownika przez system teleinformatyczny Serwisu"]),
          item([
            bold("Cookies zewnętrzne"),
            " - pliki zamieszczane i odczytywane z Urządzenia Użytkownika przez systemy teleinformatyczne Serwisów zewnętrznych. Skrypty Serwisów zewnętrznych, które mogą umieszczać pliki Cookies na Urządzeniach Użytkownika, zostały świadomie umieszczone w Serwisie poprzez skrypty i usługi udostępnione i zainstalowane w Serwisie",
          ]),
          item([
            bold("Cookies sesyjne"),
            " - pliki zamieszczane i odczytywane z Urządzenia Użytkownika przez Serwis lub Serwisy zewnętrzne podczas jednej sesji danego Urządzenia. Po zakończeniu sesji pliki są usuwane z Urządzenia Użytkownika.",
          ]),
          item([
            bold("Cookies trwałe"),
            " - pliki zamieszczane i odczytywane z Urządzenia Użytkownika przez Serwis lub Serwisy zewnętrzne do momentu ich ręcznego usunięcia. Pliki nie są usuwane automatycznie po zakończeniu sesji Urządzenia, chyba że konfiguracja Urządzenia Użytkownika jest ustawiona na tryb usuwania plików Cookie po zakończeniu sesji Urządzenia.",
          ]),
        ),
      ],
    },
    {
      title: "§4 Bezpieczeństwo składowania danych",
      blocks: [
        ul(
          item([
            bold("Mechanizmy składowania i odczytu plików Cookie"),
            " - Mechanizmy składowania, odczytu i wymiany danych pomiędzy plikami Cookies zapisywanymi na Urządzeniu Użytkownika a Serwisem są realizowane poprzez wbudowane mechanizmy przeglądarek internetowych i nie pozwalają na pobieranie innych danych z Urządzenia Użytkownika lub danych innych witryn internetowych, które odwiedzał Użytkownik, w tym danych osobowych ani informacji poufnych.",
          ]),
          item([
            bold("Cookie wewnętrzne"),
            " - zastosowane przez Administratora pliki Cookie są bezpieczne dla Urządzeń Użytkowników i nie zawierają skryptów, treści lub informacji mogących zagrażać bezpieczeństwu danych osobowych lub bezpieczeństwu Urządzenia, z którego korzysta Użytkownik.",
          ]),
          item([
            bold("Cookie zewnętrzne"),
            " - Administrator dokonuje wszelkich możliwych działań w celu weryfikacji i doboru partnerów serwisu w kontekście bezpieczeństwa Użytkowników. Lista partnerów zamieszczona jest w dalszej części Polityki Prywatności.",
          ]),
          item(
            [bold("Kontrola plików Cookie")],
            [
              item(["Użytkownik może w dowolnym momencie samodzielnie zmienić ustawienia dotyczące zapisywania, usuwania oraz dostępu do danych zapisanych plików Cookies przez każdą witrynę internetową."]),
              item(
                [
                  "Informacje o sposobie wyłączenia plików Cookie w najpopularniejszych przeglądarkach komputerowych dostępne są na stronie: ",
                  { link: "jak wyłączyć cookie", href: "https://nety.pl/jak-wylaczyc-pliki-cookie/", external: true },
                  " lub u jednego ze wskazanych dostawców:",
                ],
                [
                  item([{ link: "Zarządzanie plikami cookies w przeglądarce Chrome", href: "https://support.google.com/accounts/answer/61416?co=GENIE.Platform%3DDesktop&hl=pl", external: true }]),
                  item([{ link: "Zarządzanie plikami cookies w przeglądarce Opera", href: "https://help.opera.com/pl/latest/web-preferences/", external: true }]),
                  item([{ link: "Zarządzanie plikami cookies w przeglądarce FireFox", href: "https://support.mozilla.org/pl/kb/blokowanie-ciasteczek", external: true }]),
                  item([{ link: "Zarządzanie plikami cookies w przeglądarce Edge", href: "https://support.microsoft.com/pl-pl/help/4027947/microsoft-edge-delete-cookies", external: true }]),
                  item([{ link: "Zarządzanie plikami cookies w przeglądarce Safari", href: "https://support.apple.com/pl-pl/guide/safari/sfri11471/mac", external: true }]),
                ],
              ),
              item([
                "Użytkownik może w dowolnym momencie usunąć wszelkie zapisane do tej pory pliki Cookie korzystając z narzędzi Urządzenia Użytkownika, za pośrednictwem którego Użytkownik korzysta z usług Serwisu.",
              ]),
            ],
          ),
          item([
            bold("Zagrożenia po stronie Użytkownika"),
            " - Administrator stosuje wszelkie możliwe środki techniczne w celu zapewnienia bezpieczeństwa danych umieszczanych w plikach Cookie. Należy jednak zwrócić uwagę, że zapewnienie bezpieczeństwa tych danych zależy także od działalności Użytkownika. Użytkownicy w celu zabezpieczenia się przed zagrożeniami powinni stosować się do ",
            { link: "zaleceń korzystania z sieci", href: "https://nety.pl/cyberbezpieczenstwo/", external: true },
            ".",
          ]),
          item([
            bold("Przechowywanie danych osobowych"),
            " - Administrator zapewnia, że dokonuje wszelkich starań, by przetwarzane dane osobowe wprowadzone dobrowolnie przez Użytkowników były bezpieczne, dostęp do nich był ograniczony i realizowany zgodnie z ich przeznaczeniem i celami przetwarzania.",
          ]),
        ),
      ],
    },
    {
      title: "§5 Cele, do których wykorzystywane są pliki Cookie",
      blocks: [
        ul(
          item(["Usprawnienie i ułatwienie dostępu do Serwisu, w tym utrzymanie sesji personelu lokalu"]),
          item(["Personalizacja Serwisu dla Użytkowników, w tym zapamiętanie języka interfejsu i motywu wyświetlania"]),
          item(["Prowadzenie statystyk (użytkowników, ilości odwiedzin, rodzajów urządzeń, łącza itp.)"]),
          item(["Analiza zachowania Użytkowników w celu usprawnienia Serwisu (mapy ciepła, nagrania sesji, ankiety)"]),
          item(["Zapewnienie bezpieczeństwa i integralności Serwisu"]),
        ),
      ],
    },
    {
      title: "§6 Cele przetwarzania danych osobowych",
      blocks: [
        p("Dane osobowe dobrowolnie podane przez Użytkowników są przetwarzane w jednym z następujących celów:"),
        ul(
          item(
            [bold("Realizacji usług elektronicznych:")],
            [
              item(["rejestracja i obsługa kont personelu lokali gastronomicznych"]),
              item(["składanie i obsługa zamówień online oraz płatności"]),
              item(["śledzenie statusu zamówienia i wysyłka powiadomień"]),
              item(["integracja płatności Stripe Connect dla lokali"]),
            ],
          ),
          item(["Komunikacji Administratora z Użytkownikami w sprawach związanych z Serwisem oraz ochroną danych"]),
          item(["Zapewnienia prawnie uzasadnionego interesu Administratora"]),
        ),
        p("Dane o Użytkownikach gromadzone anonimowo i automatycznie są przetwarzane w jednym z następujących celów:"),
        ul(
          item(["Prowadzenie statystyk i analizy ruchu w Serwisie"]),
          item(["Diagnostyka błędów i utrzymanie niezawodności Serwisu"]),
          item(["Zapewnienia prawnie uzasadnionego interesu Administratora"]),
        ),
      ],
    },
    {
      title: "§7 Pliki Cookies Serwisów zewnętrznych",
      blocks: [
        p(
          "Administrator w Serwisie wykorzystuje lub może wykorzystywać skrypty javascript i komponenty webowe partnerów, którzy mogą umieszczać własne pliki cookies na Urządzeniu Użytkownika. Poniżej znajduje się lista partnerów lub ich usług zaimplementowanych w Serwisie lub planowanych do wdrożenia, mogących umieszczać pliki cookies:",
        ),
        ul(
          item(
            [bold("Prowadzenie statystyk:")],
            [
              item([{ link: "Google Analytics", href: "https://policies.google.com/privacy?hl=pl", external: true }]),
              item([{ link: "Hotjar", href: "https://www.hotjar.com/legal/policies/privacy/", external: true }]),
            ],
          ),
          item(
            [bold("Zarządzanie tagami:")],
            [item([{ link: "Google Tag Manager", href: "https://policies.google.com/privacy?hl=pl", external: true }])],
          ),
          item(
            [bold("Obsługa płatności:")],
            [item([{ link: "Stripe", href: "https://stripe.com/pl/privacy", external: true }])],
          ),
        ),
        p(
          "Usługi świadczone przez podmioty trzecie są poza kontrolą Administratora. Podmioty te mogą w każdej chwili zmienić swoje warunki świadczenia usług, polityki prywatności, cel przetwarzania danych oraz sposoby wykorzystywania plików cookie.",
        ),
      ],
    },
    {
      title: "§8 Rodzaje gromadzonych danych",
      blocks: [
        p(
          "Serwis gromadzi dane o Użytkownikach. Część danych jest gromadzona automatycznie, a część danych to dane osobowe podane dobrowolnie przez Użytkowników w trakcie korzystania z usług oferowanych przez Serwis.",
        ),
        p(bold("Dane gromadzone automatycznie (logi serwera i diagnostyka):")),
        ul(
          item(["Adres IP"]),
          item(["Typ przeglądarki"]),
          item(["Rozdzielczość ekranu"]),
          item(["Rodzaj systemu operacyjnego"]),
          item(["Przybliżona lokalizacja"]),
          item(["Otwierane podstrony serwisu"]),
          item(["Czas spędzony na odpowiedniej podstronie serwisu"]),
          item(["Adres poprzedniej podstrony"]),
          item(["Adres strony odsyłającej"]),
          item(["Język przeglądarki"]),
          item(["Prędkość łącza internetowego"]),
          item(["Dostawca usług internetowych"]),
          item(["Identyfikatory żądań HTTP i znaczniki czasu"]),
        ),
        p(bold("Dane gromadzone przez narzędzia analityczne i badania UX (np. Google Analytics, Hotjar):")),
        ul(
          item(["Interakcje ze stroną, takie jak kliknięcia, przewijanie i ruch wskaźnika"]),
          item(["Nagrania sesji i mapy ciepła (po wyłączeniu wrażliwych pól przez Administratora)"]),
          item(["Identyfikatory urządzenia lub sesji nadawane przez narzędzie analityczne"]),
        ),
        p(bold("Dane gromadzone podczas rejestracji konta personelu:")),
        ul(
          item(["Adres e-mail"]),
          item(["Hasło (przechowywane w postaci zahaszowanej)"]),
          item(["Imię i nazwisko lub pseudonim"]),
          item(["Przypisanie do lokalu i rola w zespole"]),
          item(["Adres IP (zbierane automatycznie)"]),
        ),
        p(bold("Dane gromadzone podczas składania zamówienia przez gościa lokalu:")),
        ul(
          item(["Pozycje zamówienia, ilości i notatki"]),
          item(["Opcjonalny adres e-mail klienta"]),
          item(["Identyfikator miejsca lub stolika"]),
          item(["Dane płatności przetwarzane przez Stripe (Administrator nie przechowuje pełnych danych karty)"]),
          item(["Token śledzenia zamówienia"]),
          item(["Adres IP (zbierane automatycznie)"]),
        ),
        p(bold("Dane gromadzone po wyrażeniu zgody na powiadomienia push:")),
        ul(item(["Adres endpointu subskrypcji Web Push"])),
        p(
          "Część danych (bez danych identyfikujących) może być przechowywana w pamięci przeglądarki (localStorage, sessionStorage) lub w plikach cookies. Część danych (bez danych identyfikujących) może być przekazywana dostawcom usług statystycznych, hostingowych i diagnostycznych.",
        ),
      ],
    },
    {
      title: "§9 Dostęp do danych osobowych przez podmioty trzecie",
      blocks: [
        p(
          "Co do zasady jedynym odbiorcą danych osobowych podawanych przez Użytkowników jest Administrator. Dane gromadzone w ramach świadczonych usług nie są odsprzedawane podmiotom trzecim.",
        ),
        p(
          "Dostęp do danych (najczęściej na podstawie umowy powierzenia przetwarzania danych) mogą posiadać podmioty odpowiedzialne za utrzymanie infrastruktury i usług niezbędnych do prowadzenia Serwisu, tj.:",
        ),
        ul(
          item(["Google Cloud Platform (hosting aplikacji i baza danych, region UE)"]),
          item(["Google LLC (Google Analytics, Google Tag Manager)"]),
          item(["Hotjar Ltd. (analiza zachowania użytkowników i badania UX)"]),
          item(["Stripe, Inc. (obsługa płatności online)"]),
          item(["Functional Software, Inc. / Sentry (monitoring błędów aplikacji)"]),
          item(["Dostawca poczty elektronicznej (wysyłka wiadomości transakcyjnych, np. potwierdzenia konta)"]),
        ),
      ],
    },
    {
      title: "§10 Sposób przetwarzania danych osobowych",
      blocks: [
        p(bold("Dane osobowe podane dobrowolnie przez Użytkowników:")),
        ul(
          item([
            "Dane osobowe nie będą przekazywane poza Unię Europejską, chyba że jest to wymagane do realizacji płatności lub diagnostyki błędów przez wskazanych procesorów działających z odpowiednimi zabezpieczeniami.",
          ]),
          item(["Dane osobowe nie będą wykorzystywane do zautomatyzowanego podejmowania decyzji (profilowania)."]),
          item(["Dane osobowe nie będą odsprzedawane podmiotom trzecim."]),
        ),
        p(bold("Dane anonimowe (bez danych osobowych) gromadzone automatycznie:")),
        ul(
          item(["Dane anonimowe mogą być przekazywane poza Unię Europejską w ramach usług diagnostycznych."]),
          item(["Dane anonimowe nie będą wykorzystywane do zautomatyzowanego podejmowania decyzji (profilowania)."]),
          item(["Dane anonimowe nie będą odsprzedawane podmiotom trzecim."]),
        ),
      ],
    },
    {
      title: "§11 Podstawy prawne przetwarzania danych osobowych",
      blocks: [
        p("Serwis gromadzi i przetwarza dane Użytkowników na podstawie:"),
        ul(
          item(
            [
              "Rozporządzenia Parlamentu Europejskiego i Rady (UE) 2016/679 z dnia 27 kwietnia 2016 r. w sprawie ochrony osób fizycznych w związku z przetwarzaniem danych osobowych i w sprawie swobodnego przepływu takich danych oraz uchylenia dyrektywy 95/46/WE (ogólne rozporządzenie o ochronie danych):",
            ],
            [
              item(["art. 6 ust. 1 lit. a - zgoda osoby, której dane dotyczą"]),
              item(["art. 6 ust. 1 lit. b - wykonanie umowy lub działania przed zawarciem umowy"]),
              item(["art. 6 ust. 1 lit. f - prawnie uzasadnione interesy Administratora"]),
            ],
          ),
          item(["Ustawy z dnia 10 maja 2018 r. o ochronie danych osobowych"]),
          item(["Ustawy z dnia 16 lipca 2004 r. Prawo telekomunikacyjne"]),
        ),
      ],
    },
    {
      title: "§12 Okres przetwarzania danych osobowych",
      blocks: [
        p(bold("Dane osobowe podane dobrowolnie przez Użytkowników:")),
        p(
          "Co do zasady wskazane dane osobowe są przechowywane przez okres świadczenia Usługi w ramach Serwisu przez Administratora. Dane kont personelu są usuwane lub anonimizowane w okresie do 30 dni od chwili usunięcia konta. Dane zamówień są przechowywane przez czas niezbędny do realizacji zamówienia, rozliczeń oraz ewentualnej obrony roszczeń.",
        ),
        p(
          "Wyjątek stanowi sytuacja wymagająca zabezpieczenia prawnie uzasadnionych celów dalszego przetwarzania danych przez Administratora. W takiej sytuacji Administrator będzie przechowywał wskazane dane, od czasu żądania ich usunięcia przez Użytkownika, nie dłużej niż przez okres 3 lat w przypadku naruszenia lub podejrzenia naruszenia zapisów regulaminu serwisu przez Użytkownika.",
        ),
        p(bold("Dane anonimowe (bez danych osobowych) gromadzone automatycznie:")),
        p(
          "Anonimowe dane statystyczne i diagnostyczne, niestanowiące danych osobowych, są przechowywane przez Administratora przez czas nieoznaczony w zakresie niezbędnym do utrzymania Serwisu.",
        ),
      ],
    },
    {
      title: "§13 Prawa Użytkowników związane z przetwarzaniem danych osobowych",
      blocks: [
        ul(
          item(
            [bold("Prawo dostępu do danych osobowych")],
            [item(["Użytkownikom przysługuje prawo uzyskania dostępu do swoich danych osobowych, realizowane na żądanie złożone do Administratora."])],
          ),
          item(
            [bold("Prawo do sprostowania danych osobowych")],
            [item(["Użytkownikom przysługuje prawo żądania od Administratora niezwłocznego sprostowania danych osobowych, które są nieprawidłowe lub uzupełnienia niekompletnych danych osobowych."])],
          ),
          item(
            [bold("Prawo do usunięcia danych osobowych")],
            [
              item([
                "Użytkownikom przysługuje prawo żądania od Administratora niezwłocznego usunięcia danych osobowych, realizowane na żądanie złożone do Administratora. W przypadku kont personelu usunięcie danych polega na anonimizacji danych umożliwiających identyfikację Użytkownika. Administrator zastrzega sobie prawo wstrzymania realizacji żądania usunięcia danych w celu ochrony prawnie uzasadnionego interesu Administratora.",
              ]),
            ],
          ),
          item(
            [bold("Prawo do ograniczenia przetwarzania danych osobowych")],
            [item(["Użytkownikom przysługuje prawo ograniczenia przetwarzania danych osobowych w przypadkach wskazanych w art. 18 RODO, realizowane na żądanie złożone do Administratora."])],
          ),
          item(
            [bold("Prawo do przenoszenia danych osobowych")],
            [item(["Użytkownikom przysługuje prawo uzyskania od Administratora danych osobowych dotyczących Użytkownika w ustrukturyzowanym, powszechnie używanym formacie nadającym się do odczytu maszynowego."])],
          ),
          item(
            [bold("Prawo wniesienia sprzeciwu wobec przetwarzania danych osobowych")],
            [item(["Użytkownikom przysługuje prawo wniesienia sprzeciwu wobec przetwarzania ich danych osobowych w przypadkach określonych w art. 21 RODO."])],
          ),
          item(
            [bold("Prawo wniesienia skargi")],
            [item(["Użytkownikom przysługuje prawo wniesienia skargi do organu nadzorczego zajmującego się ochroną danych osobowych (Prezes Urzędu Ochrony Danych Osobowych)."])],
          ),
        ),
      ],
    },
    {
      title: "§14 Kontakt do Administratora",
      blocks: [
        p("Z Administratorem można skontaktować się w jeden z poniższych sposobów:"),
        ul(
          item([bold("Adres pocztowy"), " - Liftium Sp. z o.o., ul. Floriańska 2/06, 03-707 Warszawa, Polska"]),
          item([bold("Adres poczty elektronicznej"), " - contact.liftium@gmail.com"]),
        ),
      ],
    },
    {
      title: "§15 Wymagania Serwisu",
      blocks: [
        ul(
          item([
            "Ograniczenie zapisu i dostępu do plików Cookie na Urządzeniu Użytkownika może spowodować nieprawidłowe działanie niektórych funkcji Serwisu, w tym logowania personelu lokalu.",
          ]),
          item([
            "Administrator nie ponosi odpowiedzialności za nieprawidłowo działające funkcje Serwisu w przypadku, gdy Użytkownik ograniczy w jakikolwiek sposób możliwość zapisywania i odczytu plików Cookie.",
          ]),
        ),
      ],
    },
    {
      title: "§16 Linki zewnętrzne",
      blocks: [
        p(
          "W Serwisie mogą znajdować się odnośniki do witryn zewnętrznych, w tym do operatorów płatności, z którymi Administrator współpracuje. Linki te oraz strony lub pliki pod nimi wskazane mogą podlegać odrębnym politykom prywatności. Administrator nie ponosi odpowiedzialności za zawartość znajdującą się poza Serwisem.",
        ),
      ],
    },
    {
      title: "§17 Zmiany w Polityce Prywatności",
      blocks: [
        ul(
          item([
            "Administrator zastrzega sobie prawo do dowolnej zmiany niniejszej Polityki Prywatności bez konieczności informowania o tym Użytkowników w zakresie stosowania i wykorzystywania danych anonimowych lub stosowania plików Cookie.",
          ]),
          item([
            "Administrator zastrzega sobie prawo do dowolnej zmiany niniejszej Polityki Prywatności w zakresie przetwarzania danych osobowych, o czym poinformuje Użytkowników posiadających konta personelu za pośrednictwem poczty elektronicznej w terminie do 7 dni od zmiany zapisów. Dalsze korzystanie z usług oznacza zapoznanie się i akceptację wprowadzonych zmian Polityki Prywatności.",
          ]),
          item(["Wprowadzone zmiany w Polityce Prywatności będą publikowane na tej podstronie Serwisu."]),
          item(["Wprowadzone zmiany wchodzą w życie z chwilą ich publikacji."]),
        ),
      ],
    },
  ];
}

function privacySectionsEn(): PrivacySection[] {
  return [
    {
      title: "Introduction",
      blocks: [
        p(
          "This Privacy Policy sets out ",
          bold("how data is stored and accessed on Users' Devices"),
          " when using the Service for electronic services provided by the Controller, and ",
          bold("how Users' personal data is collected and processed"),
          " when voluntarily provided through tools available in the Service.",
        ),
        p(
          "This Privacy Policy is an integral part of the ",
          { link: "Terms of Service", href: TERMS_HREF, external: true },
          ", which sets out the rules, rights, and obligations of Users of the Service.",
        ),
      ],
    },
    {
      title: "§1 Definitions",
      blocks: [
        ul(
          item([
            bold("Service"),
            ' - the "barmaid" website at www.barmaid.io, enabling hospitality venue guests to place orders online and venue staff to manage menus, spots, and orders.',
          ]),
          item([bold("External service"), " - websites of partners, service providers, or recipients cooperating with the Controller"]),
          item([
            bold("Service / Data Controller"),
            ' - the Service and personal data controller (the Controller) is "Liftium Sp. z o.o.", ul. Floriańska 2/06, 03-707 Warsaw, Poland, tax ID (NIP): 1133153571, company register (KRS): 0001143654.',
          ]),
          item([
            bold("User"),
            " - a natural person using the Service, including a venue guest placing an order and venue staff with an admin account.",
          ]),
          item([bold("Device"), " - an electronic device and software used to access the Service"]),
          item([bold("Cookies"), " - text data stored in files placed on the User's Device"]),
          item([bold("GDPR"), " - Regulation (EU) 2016/679 on the protection of natural persons with regard to the processing of personal data"]),
          item([bold("Personal data"), " - information relating to an identified or identifiable natural person"]),
          item([bold("Processing"), " - any operation performed on personal data, whether automated or not"]),
          item([bold("Restriction of processing"), " - marking stored personal data to limit future processing"]),
          item([bold("Profiling"), " - automated processing to evaluate personal aspects relating to a natural person"]),
          item([bold("Consent"), " - a freely given, specific, informed, and unambiguous indication of the data subject's wishes"]),
          item([bold("Personal data breach"), " - a breach of security leading to accidental or unlawful destruction, loss, alteration, or unauthorized disclosure of personal data"]),
          item([bold("Pseudonymization"), " - processing personal data so it can no longer be attributed to a specific data subject without additional information"]),
          item([bold("Anonymization"), " - an irreversible process that destroys or overwrites personal data so records can no longer be linked to a specific person"]),
        ),
      ],
    },
    {
      title: "§2 Data Protection Officer",
      blocks: [
        p("Under Art. 37 GDPR, the Controller has not appointed a Data Protection Officer."),
        p("For matters relating to data processing, including personal data, contact the Controller directly."),
      ],
    },
    {
      title: "§3 Types of cookies",
      blocks: [
        ul(
          item([bold("Internal cookies"), " - files placed and read from the User's Device by the Service"]),
          item([bold("External cookies"), " - files placed and read from the User's Device by external services whose scripts are intentionally embedded in the Service"]),
          item([bold("Session cookies"), " - files placed during a single Device session and removed when the session ends"]),
          item([bold("Persistent cookies"), " - files stored until manually deleted or until the browser is configured to clear cookies after each session"]),
        ),
      ],
    },
    {
      title: "§4 Data storage security",
      blocks: [
        ul(
          item([
            bold("Cookie storage and read mechanisms"),
            " - Browsers handle cookie storage and exchange with the Service. Cookies do not allow the Service to read unrelated data from the Device or from other websites visited by the User.",
          ]),
          item([
            bold("Internal cookies"),
            " - Cookies used by the Controller are intended to be safe for Users' Devices and do not contain scripts or content that threaten personal data or Device security.",
          ]),
          item([
            bold("External cookies"),
            " - The Controller takes reasonable steps to select partners with regard to User security. A list of partners appears later in this Privacy Policy.",
          ]),
          item(
            [bold("Cookie controls")],
            [
              item(["Users may change cookie settings for any website at any time."]),
              item(
                [
                  "Guides for disabling cookies in popular browsers are available at ",
                  { link: "how to disable cookies", href: "https://nety.pl/jak-wylaczyc-pliki-cookie/", external: true },
                  " or from browser vendors such as Chrome, Opera, Firefox, Edge, and Safari.",
                ],
              ),
              item(["Users may delete stored cookies using their Device tools at any time."]),
            ],
          ),
          item([
            bold("User-side risks"),
            " - The Controller applies technical measures to protect cookie data, but security also depends on User activity. Users should follow ",
            { link: "safe internet use recommendations", href: "https://nety.pl/cyberbezpieczenstwo/", external: true },
            ".",
          ]),
          item([
            bold("Personal data storage"),
            " - The Controller makes reasonable efforts to keep voluntarily provided personal data secure, access-controlled, and processed only for stated purposes.",
          ]),
        ),
      ],
    },
    {
      title: "§5 Purposes of cookies",
      blocks: [
        ul(
          item(["Improving and facilitating access to the Service, including maintaining venue staff sessions"]),
          item(["Personalizing the Service for Users, including remembering interface language and display theme"]),
          item(["Running statistics (users, visit counts, device types, connection type, etc.)"]),
          item(["Analyzing User behavior to improve the Service (heatmaps, session recordings, surveys)"]),
          item(["Ensuring Service security and integrity"]),
        ),
      ],
    },
    {
      title: "§6 Purposes of personal data processing",
      blocks: [
        p("Personal data voluntarily provided by Users is processed for one or more of the following purposes:"),
        ul(
          item(
            [bold("Providing electronic services:")],
            [
              item(["registering and operating venue staff accounts"]),
              item(["handling online orders and payments"]),
              item(["order status tracking and notifications"]),
              item(["Stripe Connect payment integration for venues"]),
            ],
          ),
          item(["Communication between the Controller and Users about the Service and data protection"]),
          item(["Pursuing the Controller's legitimate interests"]),
        ),
        p("Data collected automatically and anonymously from Users is processed for:"),
        ul(
          item(["traffic statistics and Service usage analytics"]),
          item(["error diagnostics and Service reliability"]),
          item(["pursuing the Controller's legitimate interests"]),
        ),
      ],
    },
    {
      title: "§7 External service cookies",
      blocks: [
        p(
          "The Service uses or may use JavaScript and web components from partners that may place their own cookies on the User's Device. Partners implemented in the Service or planned for deployment that may place cookies include:",
        ),
        ul(
          item(
            [bold("Statistics:")],
            [
              item([{ link: "Google Analytics", href: "https://policies.google.com/privacy", external: true }]),
              item([{ link: "Hotjar", href: "https://www.hotjar.com/legal/policies/privacy/", external: true }]),
            ],
          ),
          item(
            [bold("Tag management:")],
            [item([{ link: "Google Tag Manager", href: "https://policies.google.com/privacy", external: true }])],
          ),
          item(
            [bold("Payment processing:")],
            [item([{ link: "Stripe", href: "https://stripe.com/privacy", external: true }])],
          ),
        ),
        p(
          "Third-party services are outside the Controller's control and may change their terms, privacy policies, processing purposes, and cookie practices at any time.",
        ),
      ],
    },
    {
      title: "§8 Types of data collected",
      blocks: [
        p("The Service collects data about Users. Some data is collected automatically; some is personal data voluntarily provided when using the Service."),
        p(bold("Automatically collected data (server logs and diagnostics):")),
        ul(
          item(["IP address"]),
          item(["Browser type"]),
          item(["Screen resolution"]),
          item(["Operating system"]),
          item(["Approximate location"]),
          item(["Pages opened within the Service"]),
          item(["Time spent on a given page"]),
          item(["Previous page address"]),
          item(["Referring page address"]),
          item(["Browser language"]),
          item(["Internet connection speed"]),
          item(["Internet service provider"]),
          item(["HTTP request identifiers and timestamps"]),
        ),
        p(bold("Data collected by analytics and UX tools (e.g. Google Analytics, Hotjar):")),
        ul(
          item(["On-page interactions such as clicks, scrolling, and pointer movement"]),
          item(["Session recordings and heatmaps (with sensitive fields masked by the Controller)"]),
          item(["Device or session identifiers assigned by the analytics tool"]),
        ),
        p(bold("Data collected when registering a staff account:")),
        ul(
          item(["Email address"]),
          item(["Password (stored hashed)"]),
          item(["Name or display name"]),
          item(["Venue assignment and team role"]),
          item(["IP address (collected automatically)"]),
        ),
        p(bold("Data collected when a venue guest places an order:")),
        ul(
          item(["Order items, quantities, and notes"]),
          item(["Optional customer email"]),
          item(["Spot or table identifier"]),
          item(["Payment data processed by Stripe (the Controller does not store full card details)"]),
          item(["Order tracking token"]),
          item(["IP address (collected automatically)"]),
        ),
        p(bold("Data collected after consent to push notifications:")),
        ul(item(["Web Push subscription endpoint address"])),
        p(
          "Some non-identifying data may be stored in browser memory (localStorage, sessionStorage) or in cookies. Some non-identifying data may be shared with statistics, hosting, and diagnostics providers.",
        ),
      ],
    },
    {
      title: "§9 Third-party access to personal data",
      blocks: [
        p("As a rule, the Controller is the only recipient of personal data provided by Users. Data collected to provide the Service is not sold to third parties."),
        p("The following entities may access data (typically under a data processing agreement) to maintain infrastructure and services required to operate the Service:"),
        ul(
          item(["Google Cloud Platform (application hosting and database, EU region)"]),
          item(["Google LLC (Google Analytics, Google Tag Manager)"]),
          item(["Hotjar Ltd. (user behavior analysis and UX research)"]),
          item(["Stripe, Inc. (online payment processing)"]),
          item(["Functional Software, Inc. / Sentry (application error monitoring)"]),
          item(["Email provider (transactional messages, e.g. account confirmations)"]),
        ),
      ],
    },
    {
      title: "§10 How personal data is processed",
      blocks: [
        p(bold("Personal data voluntarily provided by Users:")),
        ul(
          item(["Personal data will not be transferred outside the European Union unless required for payment processing or error diagnostics by listed processors with appropriate safeguards."]),
          item(["Personal data will not be used for automated decision-making (profiling)."]),
          item(["Personal data will not be sold to third parties."]),
        ),
        p(bold("Automatically collected anonymous data (without personal data):")),
        ul(
          item(["Anonymous data may be transferred outside the European Union as part of diagnostics services."]),
          item(["Anonymous data will not be used for automated decision-making (profiling)."]),
          item(["Anonymous data will not be sold to third parties."]),
        ),
      ],
    },
    {
      title: "§11 Legal bases for processing",
      blocks: [
        p("The Service collects and processes User data on the basis of:"),
        ul(
          item(
            [bold("GDPR (Regulation (EU) 2016/679):")],
            [
              item(["Art. 6(1)(a) - consent of the data subject"]),
              item(["Art. 6(1)(b) - performance of a contract or steps prior to entering a contract"]),
              item(["Art. 6(1)(f) - legitimate interests pursued by the Controller"]),
            ],
          ),
          item([bold("Polish Act of 10 May 2018 on personal data protection")]),
          item([bold("Polish Telecommunications Law of 16 July 2004")]),
        ),
      ],
    },
    {
      title: "§12 Retention periods",
      blocks: [
        p(bold("Personal data voluntarily provided by Users:")),
        p(
          "As a rule, such personal data is stored for as long as the Service is provided. Staff account data is deleted or anonymized within 30 days of account deletion. Order data is kept as long as needed to fulfill the order, settle payments, and defend potential claims.",
        ),
        p(
          "The Controller may retain data longer when necessary to protect legitimate interests, for up to 3 years after a deletion request where the User breached or is suspected of breaching the Terms of Service.",
        ),
        p(bold("Automatically collected anonymous data:")),
        p("Anonymous statistical and diagnostic data that is not personal data may be retained indefinitely as needed to maintain the Service."),
      ],
    },
    {
      title: "§13 User rights",
      blocks: [
        ul(
          item([bold("Right of access"), " - Users may request access to their personal data from the Controller."]),
          item([bold("Right to rectification"), " - Users may request correction or completion of inaccurate or incomplete personal data."]),
          item([
            bold("Right to erasure"),
            " - Users may request deletion of personal data. For staff accounts, deletion means anonymizing identifying data. The Controller may delay deletion to protect legitimate interests.",
          ]),
          item([bold("Right to restriction"), " - Users may request restriction of processing under Art. 18 GDPR."]),
          item([bold("Right to data portability"), " - Users may request a structured, machine-readable copy of their personal data."]),
          item([bold("Right to object"), " - Users may object to processing under Art. 21 GDPR."]),
          item([bold("Right to lodge a complaint"), " - Users may lodge a complaint with the Polish data protection authority (UODO)."]),
        ),
      ],
    },
    {
      title: "§14 Contacting the Controller",
      blocks: [
        p("You can contact the Controller by:"),
        ul(
          item([bold("Postal address"), " - Liftium Sp. z o.o., ul. Floriańska 2/06, 03-707 Warsaw, Poland"]),
          item([bold("Email"), " - contact.liftium@gmail.com"]),
        ),
      ],
    },
    {
      title: "§15 Service requirements",
      blocks: [
        ul(
          item(["Restricting cookies on the User's Device may cause some Service features to malfunction, including venue staff login."]),
          item(["The Controller is not responsible for malfunctioning features if the User restricts cookie storage or access."]),
        ),
      ],
    },
    {
      title: "§16 External links",
      blocks: [
        p(
          "The Service may contain links to external websites, including payment operators cooperating with the Controller. Those sites are subject to their own privacy policies. The Controller is not responsible for content outside the Service.",
        ),
      ],
    },
    {
      title: "§17 Changes to this Privacy Policy",
      blocks: [
        ul(
          item(["The Controller may change this Privacy Policy regarding anonymous data or cookies without notifying Users."]),
          item([
            "The Controller may change this Privacy Policy regarding personal data and will notify staff account holders by email within 7 days of the change. Continued use means acceptance of the updated Privacy Policy.",
          ]),
          item(["Changes will be published on this page of the Service."]),
          item(["Changes take effect when published."]),
        ),
      ],
    },
  ];
}
